import axios from 'axios';
import { ANALYTICS_API_BASE } from '../Config/Config';
import { UpdateAnalyticsProfile, GetAnalyticsProfile } from './HeedifyServices';

/**
 * Execute a predefined analytics query
 * @param {string} tenantId - Tenant identifier
 * @param {string} queryId - Query ID from predefined-queries.json (e.g., "total-calls")
 * @param {object} filters - Filter values (dateRange, queueId, direction, etc.)
 * @param {string} token - Authorization token
 * @returns {Promise<object>} Query results
 */
export async function executeAnalyticsQuery(tenantId, queryId, filters = {}, token) {
    try {
        const url = `${ANALYTICS_API_BASE}/query/${tenantId}`;

        const requestBody = {
            queryId,
            filters: {
                ...filters,
                tenantId // Ensure tenantId is in filters
            }
        };

        const response = await axios.post(url, requestBody, {
            headers: {
                'Content-Type': 'application/json',
                ...(token && { 'Authorization': `Bearer ${token}` })
            },
            timeout: 30000 // 30 secondes pour Cosmos DB (beaucoup plus rapide que Synapse)
        });

        if (response.data.success) {
            return {
                success: true,
                data: response.data.data || response.data.result, // Support both formats
                metadata: response.data.metadata
            };
        } else {
            return {
                success: false,
                error: response.data.error
            };
        }
    } catch (error) {
        return {
            success: false,
            error: error.response?.data?.error || error.message || 'Unknown error'
        };
    }
}

/**
 * Fetch data for a widget configuration
 * @param {object} widgetConfig - Widget configuration including queryId
 * @param {object} filterValues - Current filter values from dashboard
 * @param {string} tenantId - Tenant identifier
 * @param {string} token - Authorization token
 * @returns {Promise<any>} Widget data in appropriate format
 */
export async function fetchWidgetData(widgetConfig, filterValues, tenantId, token) {
    try {
      console.log("🔍 Fetching data for widget (Query ID: ${widgetConfig || widgetConfig.query}):", widgetConfig);
        // Extract queryId from widget config
        const queryId = widgetConfig.queryId || widgetConfig.query;
console.log(`🔍 Fetching data for widget with filters:`, widgetConfig);
        if (!queryId) {
            throw new Error('Widget configuration missing queryId');
        }

        // Merge widget-specific filters with global filters
        const filters = {
            ...filterValues,
            ...widgetConfig.filters,
            // Include thresholds from widget config (abandonedThreshold, fastThreshold, slowThreshold, slaThreshold, etc.)
            ...(widgetConfig.config?.abandonedThreshold !== undefined && { abandonedThreshold: widgetConfig.config.abandonedThreshold }),
            ...(widgetConfig.config?.fastThreshold !== undefined && { fastThreshold: widgetConfig.config.fastThreshold }),
            ...(widgetConfig.config?.slowThreshold !== undefined && { slowThreshold: widgetConfig.config.slowThreshold }),
            ...(widgetConfig.config?.slaThreshold !== undefined && { slaThreshold: widgetConfig.config.slaThreshold })
        };

        // Log filters for threshold-related widgets
        if (queryId.includes('threshold') || queryId.includes('response-time')) {
            console.log(`📤 Sending to API - Query: ${queryId}`, {
                scope: filters.scope,
                thresholds: {
                    quickAnswerThreshold: filters.quickAnswerThreshold,
                    standardAnswerThreshold: filters.standardAnswerThreshold,
                    delayedAnswerThreshold: filters.delayedAnswerThreshold,
                    quickDropThreshold: filters.quickDropThreshold
                },
                allFilters: filters
            });
        }

        // Execute the query
        const result = await executeAnalyticsQuery(tenantId, queryId, filters, token);

        if (!result.success) {
            throw new Error(result.error);
        }

        // Transform data based on widget type if needed
        return transformDataForWidget(result.data, widgetConfig.type);
    } catch (error) {
        throw error;
    }
}

/**
 * Transform query results to match widget expectations
 * @param {any} data - Raw query results (typically array of rows from SQL)
 * @param {string} widgetType - Widget type (metric, bar, line, etc.)
 * @returns {any} Transformed data
 */
function transformDataForWidget(data, widgetType) {
    // Handle null/undefined data
    if (!data) {
        return null;
    }

    // SQL queries return arrays of rows - transform based on widget type
    switch (widgetType) {
        case 'metric':
            // Metric widgets expect {value, trend} object
            // SQL query returns [{value: 123, trend: 5.2}]
            if (Array.isArray(data) && data.length > 0) {
                return data[0]; // Return first row as object
            }
            return { value: 0, trend: 0 };

        case 'bar':
        case 'line':
        case 'area':
        case 'pie':
        case 'donut':
            // Chart widgets expect array of {name, value} objects
            // SQL already returns in this format
            return Array.isArray(data) ? data : [];

        case 'table':
            // Table widgets expect array of objects
            return Array.isArray(data) ? data : [];

        case 'gauge':
            // Gauge expects {value, min, max}
            if (Array.isArray(data) && data.length > 0) {
                return data[0];
            }
            return { value: 0, min: 0, max: 100 };

        default:
            // Return data as-is for unknown types
            return data;
    }
}

/**
 * Get available queries from the backend
 * @returns {Promise<object>} Available queries catalog
 */
export async function getAvailableQueries() {
    try {
        // This could be an API call to get the queries catalog
        // For now, we'll use a static import or hardcoded list
        return {
            success: true,
            queries: [] // TODO: fetch from backend or import from schema
        };
    } catch (error) {
        return {
            success: false,
            error: error.message
        };
    }
}

// ------------------ ANALYTICS LAYOUT ------------------
// Old Portal Settings methods removed - now using Analytics Profile service only

/**
 * Save Dashboard Layout (tabs, widgets, positions) to Analytics Profile
 * @param {string} tenantId - Tenant ID
 * @param {string} analyticsProfileId - Analytics Profile ID
 * @param {Object} dashboardLayout - Complete dashboard layout object
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Response with success status
 */
export async function saveDashboardLayout(tenantId, analyticsProfileId, dashboardLayout, token) {
  try {
    console.log('🔗 saveDashboardLayout called with:', { tenantId, analyticsProfileId, hasToken: !!token });
    
    if (!analyticsProfileId) {
      console.error('❌ analyticsProfileId is missing!');
      return {
        success: false,
        message: 'Analytics Profile ID is required'
      };
    }
    
    // Récupérer le profil analytics actuel pour ne modifier que le dashboardLayout
    const currentProfile = await GetAnalyticsProfile(tenantId, analyticsProfileId, token);
    
    if (!currentProfile || !currentProfile.success) {
      console.error('❌ Failed to get current analytics profile');
      return {
        success: false,
        message: 'Failed to retrieve analytics profile'
      };
    }
    
    // Construire l'objet à mettre à jour avec le dashboardLayout
    const updatedProfile = {
      ...currentProfile.data,
      dashboardLayout: {
        tabs: dashboardLayout.tabs || {}
      }
    };
    
    console.log('📦 Updating analytics profile with dashboardLayout');
    
    // Utiliser UpdateAnalyticsProfile de HeedifyServices
    const response = await UpdateAnalyticsProfile(tenantId, analyticsProfileId, updatedProfile, token);

    if (response && response.success) {
      return {
        success: true,
        message: 'Dashboard layout saved successfully',
        data: response.data
      };
    } else {
      return {
        success: false,
        message: response?.message || 'Failed to save dashboard layout'
      };
    }
  } catch (error) {
    console.error('Error saving dashboard layout:', error);
    return {
      success: false,
      message: error.response?.data?.message || error.message
    };
  }
}

/**
 * Get Dashboard Layout from Analytics Profile
 * @param {string} tenantId - Tenant ID
 * @param {string} analyticsProfileId - Analytics Profile ID
 * @param {string} token - Auth token
 * @returns {Promise<Object>} Dashboard layout object
 */
export async function getDashboardLayout(tenantId, analyticsProfileId, token) {
  try {
    console.log('📥 getDashboardLayout called with:', { tenantId, analyticsProfileId });
    
    // Utiliser GetAnalyticsProfile de HeedifyServices
    const response = await GetAnalyticsProfile(tenantId, analyticsProfileId, token);

    if (response && response.success && response.data) {
      const dashboardLayout = response.data.dashboardLayout;
      
      // Si le dashboardLayout existe, le retourner
      if (dashboardLayout && dashboardLayout.tabs) {
        return {
          success: true,
          data: dashboardLayout
        };
      }
      
      // Sinon, retourner une structure par défaut
      console.log('ℹ️ No dashboardLayout found, returning default structure');
      return {
        success: true,
        data: {
          tabs: {
            dashboard: {
              displayName: "Dashboard",
              type: "system",
              widgets: {},
              widgetsPositions: {},
              createdAt: new Date().toISOString()
            },
            calls: {
              displayName: "Calls List",
              type: "system",
              widgets: {},
              widgetsPositions: {},
              createdAt: new Date().toISOString()
            }
          }
        }
      };
    } else {
      console.warn('⚠️ Failed to load analytics profile');
      return {
        success: false,
        message: 'Failed to load analytics profile',
        data: {
          tabs: {
            dashboard: {
              displayName: "Dashboard",
              type: "system",
              widgets: {},
              widgetsPositions: {},
              createdAt: new Date().toISOString()
            },
            calls: {
              displayName: "Calls List",
              type: "system",
              widgets: {},
              widgetsPositions: {},
              createdAt: new Date().toISOString()
            }
          }
        }
      };
    }
  } catch (error) {
    console.error('Error getting dashboard layout:', error);
    return {
      success: false,
      message: error.message,
      data: {
        tabs: {
          dashboard: {
            displayName: "Dashboard",
            type: "system",
            widgets: {},
            widgetsPositions: {},
            createdAt: new Date().toISOString()
          },
          calls: {
            displayName: "Calls List",
            type: "system",
            widgets: {},
            widgetsPositions: {},
            createdAt: new Date().toISOString()
          }
        }
      }
    };
  }
}

export default {
    executeAnalyticsQuery,
    fetchWidgetData,
    getAvailableQueries,
    saveDashboardLayout,
    getDashboardLayout
};
